import { LabelValueType } from '../../../components/AsyncFormSelect/AsyncFormSelect';
import { EventModel } from '../../earlyPayout/types/EPTypes';

export interface InheritedValue<T> {
  FromLevel: number;
  FromLevelText: string;
  Value: T;
}

export interface InheritedTemplateValue<T> extends InheritedValue<T> {
  Name: string;
}

export interface MarketSetting {
  FranchiseGroupId: number;
  FranchiseId: number;
  FranchiseName: string;
  ProviderId: number;
  EntityType: number;
  EntityName: string;
  EntityId: number;
  CountryId: number;
  CountryName: string;
  IsLive: boolean;
  Enabled: boolean | null;
  InheritedEnabled?: InheritedValue<boolean>;
  SettingStatus: boolean;
  MaxBookLoss: number | null;
  InheritedMaxBookLoss?: InheritedValue<number>;
  MaxBookLossMult: number | null;
  InheritedMaxBookLossMultiplier?: InheritedValue<number>;
  BetBuilderEnabled: boolean;
  BetBuilderEnabledCount: number;
  CurrencyId: number;
  CurrencyCode?: string;
  LiveDelay: number | null;
  LiveDelayInfo: string | null;
  CashoutEnabled: boolean;
  CashoutBigM: number | null;
  CashoutLittleM: number | null;
  Tolerance: number | null;
  TemplateId: number | null;
  TemplateName: string | null;
  InheritedTemplate?: InheritedTemplateValue<number> | null;
  EnabledTemplateId: number | null;
  EnabledTemplateName: string | null;
  InheritedEnabledTemplate?: InheritedTemplateValue<number> | null;
  MarginTemplateId: number | null;
  MarginTemplateName: string | null;
  InheritedMarginTemplate?: InheritedTemplateValue<number> | null;
  MaxTemplateId: number | null;
  MaxTemplateName: string | null;
  InheritedMaxTemplate?: InheritedTemplateValue<number> | null;
  MblTemplateId: number | null;
  MblTemplateName: string | null;
  InheritedMblTemplate?: InheritedTemplateValue<number> | null;
  EnabledCount: number;
  TotalCount: number;
  TournamentTemplateId: number | null;
  TournamentTemplateName: string | null;
  InheritedTournamentTemplate: InheritedTemplateValue<number> | null;
  SportTemplateId: number | null;
  SportTemplateName: string | null;
  InheritedSportTemplate: InheritedTemplateValue<number> | null;
}

export interface MarketUnionSetting {
  FranchiseGroupId: number;
  FranchiseGroupName: string;
  FranchiseId: number;
  FranchiseName: string;
  ProviderId: number;
  EntityType: number;
  EntityName: string;
  EntityId: number;
  UnionId: number;
  MarketName: string;
  IsLive: boolean;
  Enabled: boolean;
  InheritedEnabled: InheritedValue<boolean>;
  Margin: number;
  InheritedMargin: InheritedValue<number>;
  MaxAmount: number;
  InheritedMaxAmount: InheritedValue<number>;
  MaxStakeAmount: number;
  InheritedMarginMaxStakeAmount: InheritedValue<number>;
  MaxBookLossMult: number;
  InheritedMaxBookLossMultiplier: InheritedValue<number>;
  CurrencyId: number;
  CurrencyCode: string;
  EnabledCount: number;
  TotalCount: number;
}

export interface MarketSettingsForm {
  franchiseGroup?: LabelValueType;
  franchise?: LabelValueType;
  provider: LabelValueType;
  type: LabelValueType
}

export interface MarketSettingsTableRow {
  Id: number;
  Name: string;
  MBL?: number | null;
  Setting?: MarketSetting;
}

export interface TournamentTableRow extends MarketSettingsTableRow {
  Events: EventModel[];
}

export interface TournamentTemplateOptions {
  template: LabelValueType[];
  limit: LabelValueType[];
  enable: LabelValueType[];
  margin: LabelValueType[];
  mbl: LabelValueType[];
}

export enum MARKET_SETTING_ENTITY_TYPE {
  Prematch = 'false',
  Inplay = 'true',
}

export const marketSettingsEventTypeOptions: LabelValueType[] = [
  { label: 'Prematch', value: MARKET_SETTING_ENTITY_TYPE.Prematch },
  { label: 'Inplay', value: MARKET_SETTING_ENTITY_TYPE.Inplay },
];

export interface FranchiseConfigInsert {
    FranchiseGroupId: number;
    FranchiseId: number;
    ProviderId: number;
    EntityType: number;
    EntityId: number;
    CountryId: number;
    IsLive: boolean;
    Enabled?: boolean | null;
    SettingStatus?: boolean;
    BetBuilderEnabled?: boolean;
    CurrencyId?: number;
    MaxBookLoss?: number | null;
    MaxBookLossMult?: number | null;
    LiveDelay?: number | null;
    CashoutEnabled?: boolean;
    CashoutBigM?: number;
    CashoutLittleM?: number;
    TemplateId?: number | null;
    EnabledTemplateId?: number | null;
    MarginTemplateId?: number | null;
    MaxTemplateId?: number | null;
    MblTemplateId?: number | null;
    TournamentTemplateId?: number | null;
    SportTemplateId?: number | null;
    Tolerance?: number;
    MaxTemplateName?: string;
    MblTemplateName?: string;
    SportTemplateName?: string;
    MarginTemplateName?: string;
    TournamentTemplateName?: string;
    EnabledTemplateName?: string;
    TemplateName?: string;
}

export enum FranchiseMarketSettingsEntityTypes {
  Sport = 1,
  Country = 2,
  Tournament = 3,
  Event = 4
}

export interface SaveFranchiseMarketSettingsBody {
  MarketSettings: FranchiseConfigInsert[];
}

export interface SettingsFormModel {
  [settingId: string]: MarketSetting;
}

export interface EnabledFranchiseListRequest {
  ProviderId: number;
  EntityType: number;
  EntityId: number;
  CountryId?: number;
  IsLive: boolean;
  FranchiseGroupId: number;
  FranchiseId: number;
}
export interface EnableFMSFranchise {
  CountryId: number;
  EntityId: number;
  EntityType: number;
  FranchiseGroupId: number;
  FranchiseId: number;
  FranchiseName: string;
  IsBetBuilderEnabled: boolean;
  IsEnabled: boolean;
  IsLive: boolean;
  ProviderId: number;
}

export interface FMSEnableFranchiseFormModel {
  EnabledFranchises: LabelValueType[];
  EnabledBetBuilderFranchises: LabelValueType[]
}

export interface ToggleEnabledFranchiseBody {
  ProviderId: number;
  EntityType: number;
  EntityId: number;
  IsLive: boolean;
  FranchiseIdFrom: number;
  FranchiseIdTo: number;
  Enabled?: boolean;
  BetBuilderEnabled?: boolean;
  FranchiseGroupId: number;
}

export interface FMSMarketSetting {
  FranchiseGroupId: number;
  FranchiseGroupName: string;
  FranchiseId: number;
  FranchiseName: string;
  ProviderId: number;
  EntityType: 0 | 1 | 2 | 3 | 4;
  EntityName: string;
  EntityId: number;
  UnionId: number;
  MarketName: string;
  IsLive?: boolean;
  Enabled?: boolean | null;
  Margin?: number | null;
  CurrencyId?: number;
  CurrencyCode?: string;
  MaxAmount?: number | null;
  MaxStakeAmount?: number | null;
  MaxBookLossMult?: number | string | null;
  InheritedMaxBookLossMult?: number | null;
  InheritedEnabled?: InheritedTemplateValue<boolean> | null;
  InheritedMargin?: InheritedTemplateValue<number> | null;
  InheritedMaxAmount?: InheritedTemplateValue<number> | null;
  InheritedMaxStakeAmount?: InheritedTemplateValue<number> | null;
  EnabledInfo?: string | null;
  MarginInfo?: string | null;
  MaxAmountInfo?: string | null;
  MaxStakeAmountInfo?: string | null;
  EnabledCount?: number;
  TotalCount?: number;
  UnionName?: string;
  _changed?: boolean;
  MaxBookLossMultInfo?: string;
  MaxBookLossTotalInfo?: string;
  MaxBookLossTotal?: number | null;
}

export interface GetMarketSettingsListBody {
  ProviderId: number;
  FranchiseId: number;
  SportId?: number;
  IsLive: boolean
  TournamentId?: number;
  EventId?: number;
  FranchiseGroupId: number;
  LimitsTemplateId: string | null;
  EnableTemplateId: string | null;
  MarginTemplateId: string | null;
  MblTemplateId: string | null;
}

export interface CopyFMSSettingsRequest {
  FranchiseIdFrom: number;
  FranchiseIdTo: number;
  ProviderIdFrom: number;
  ProviderIdTo: number;
  OverrideExistingSettings: boolean;
}

export interface CopyFMSSettingsFilter {
  FranchiseFrom: LabelValueType;
  FranchiseTo: LabelValueType;
  ProviderFrom: LabelValueType;
  ProviderTo: LabelValueType;
  OverrideExistingSettings?: boolean;
}

export type FieldSource = 'Default' | 'Inherited';

export enum FMSInheritEntity {
  Unknown = 0,
  Sport = 1,
  Country = 2,
  Tournament = 3,
  Event = 4,
  SportUnion = 5,
  TournamentUnion = 6,
  EventUnion = 7
}

export const UnassignOption: LabelValueType = {
  label: 'Unassign',
  value: '',
};

export interface FMSMarketsFormModel {
  Settings: FMSMarketSetting[];
  allEnabled?: boolean;
  allMargin?: number;
  allMaxAmount?: number;
  allMaxStakeAmount?: number;
  allMaxBookLossMult?: number;
  SelectedOddUnion?: {
    label: string;
    value: string;
    OddCodes: string;
    UnionName: string;
}[];
}

export interface MarketSettingsHistory {
  Id: number;
  SportManagerId: number;
  ManagerFullName: string;
  CreatedAt: string;
  FranchiseGroupId: number;
  FranchiseId: number;
  ProviderId: number;
  EntityType: number;
  EntityId: number;
  IsLive: boolean;
  Changes: string;
}

export interface MarketSettingsHistoryResponse {
  Total: string;
  Results: MarketSettingsHistory[];
}

export interface MarketSettingsHistoryRequest {
  EntityId: number;
  EntityType: number;
  IsLive: boolean;
  FranchiseGroupId?: number;
  FranchiseId?: number;
  ProviderId?: number;
  Skip?: string;
  Take?: string;
}

export type FMSPageType = 'Inplay' | 'Prematch';

export interface FMSHistoryFormModel {
  skip?: number;
  take: LabelValueType;
  currentPage: number;
}
